// OmnibarHost — the ONE command surface's impure shell (strangler-fig P2).
//
// Moved out of AppShellHost verbatim (.claude/analysis/ui-ddd-decomposition.md
// §6 Phase 2): owns the shared live query, the active-option cursor, the
// shell-style history recall, and the command-item build (library resources,
// the ambient conversation, panel commands, authoring verbs, app commands).
// Extracting this unblocks the most parallel work — the ~250-line
// command-combobox block used to sit in the file's middle, forcing every
// other diff to scroll past or rebase around it.
//
// SHAPE NOTE: unlike IngestHost (a mounted child that owns its own DOM), this
// is exported as a HOOK (`useOmnibarHost`), not a `<Component/>`. The bar
// INPUT lives inside TopStrip — a multi-concern top bar AppShellHost renders
// (back/forward, theme, account — not Omnibar's), and the RESULTS dropdown
// (`commandSurface`) is a `ReactNode` value TopStrip anchors beneath itself,
// not a child of anything Omnibar mounts. A component wrapping either would
// need a render-prop just to hand TopStrip its own props, which is more
// indirection than a hook that returns a typed API object AppShellHost
// destructures into both TopStrip and the palette in-place. State ownership
// still moves out of AppShellHost's function body — the point of the
// extraction — even though the DOM node it targets does not.
//
// Cross-feature seam (§4a): OmnibarHost never touches Editor/Chat state. On
// select it calls `openRef`/`enterFullChat`/`summonSlot` — named callbacks
// the shell supplies — never a raw `dispatch`, so this file cannot fire a
// transition outside the small set it was handed.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import {
    type KeyboardEvent as ReactKeyboardEvent,
    type ReactNode,
    useId,
    useState,
} from "react";
import type { OverlaySurface } from "../../app/frame.js";
import {
    emptyOmnibarHistory,
    type OmnibarHistory,
    recallNewer,
    recallOlder,
    record as recordOmnibarQuery,
    resetCursor as resetOmnibarCursor,
} from "../../app/omnibarHistory.js";
import type { Library } from "../../app/ports.js";
import { libraryCommandItems } from "../../app/searchCorpus.js";
import type { SecondarySlot, SlotAvailability } from "../AppShell/AppShell.js";
import type { CommandItem } from "../CommandPalette/CommandPalette.js";
import {
    CommandPalette,
    commandResultForDigit,
    commandResults,
} from "../CommandPalette/CommandPalette.js";
import type { KbdPlatform } from "../Kbd/Kbd.js";
import type {
    ResourceId,
    ResourceKind,
    ResourceRef,
} from "../Navigation/types.js";

// Every right-panel dock slot the panel-command rows iterate — OmnibarHost's
// own copy of the exhaustive `SecondarySlot` list (AppShellHost keeps a
// matching one for its ⌥-accelerator table; both are 2-element literals
// paired with the `SecondarySlot` union as their source of truth).
const secondarySlots: readonly SecondarySlot[] = [
    "assistant",
    "review",
    "grounding",
    "panel",
];

// The palette's panel-command row ids ↔ the slot each summons. The ids live
// outside the project/resource id spaces (those are server-minted), so the
// reverse lookup cannot collide with a jump row.
const panelCommandId: Record<SecondarySlot, string> = {
    assistant: "panel-assistant",
    review: "panel-review",
    grounding: "panel-grounding",
    panel: "panel-panel",
};
const panelSlotForCommandId = (id: string): SecondarySlot | undefined =>
    secondarySlots.find((slot) => panelCommandId[slot] === id);

// The palette row that opens the conversation's full tab (master audit R6:
// full is reachable from compact, the panel head, and the palette). Same id
// space as the panel commands — outside the server-minted ids.
const chatFullCommandId = "chat-full";
// The omnibar Command that opens the Settings singleton tab (P-CHROME step 4).
// Mirrors the catalogue's `open-settings` UiIntent (chord ⌘, ) as a palette
// verb — same-command: it dispatches the same open-settings the rail account
// chip and the chord fire.
const settingsCommandId = "open-settings";
// The omnibar Command that opens the History & Inspection surface — same id
// space as the other palette verbs (outside the server-minted ids).
const historyCommandId = "open-history";
// The authoring verbs (Blocker A): the omnibar becomes the real home for
// "create a thing", so a Wave-2 chrome button can be DELETED rather than
// demoted-to-nowhere. Availability-gated at build time (a palette row must never
// be a silent no-op) and routed to the SAME handlers the rail/menu already fire.
const newMaterialCommandId = "new-material";
const addSourceCommandId = "add-source";
// Start a fresh conversation — a free second entry point (Cmd+K) for the
// rail's pinned-top "new conversation" icon (regression fix: the
// chat-primary shell dropped the top strip's tab "+"). Routes to the SAME
// `new-tab` transition the rail icon / strip "+" / ⌘T already fire.
const newConversationCommandId = "new-conversation";
const exportMaterialCommandId = "export-material";
// The Diátaxis help overlay — reachable from the omnibar so it is DISCOVERABLE
// (UX-eval P2). Its own row, its own overlay.
const helpCommandId = "open-help-cmd";

// State snapshot save/load (VCR replacement, IDD f2469548): serialise the
// workspace's durable domain state to a JSON file, or seed the state cell from
// an uploaded one. Two DISTINCT verbs (distinct intents), routed to the shell's
// snapshot handlers. Ids live outside the server-minted resource id space.
const saveSnapshotCommandId = "save-snapshot";
const loadSnapshotCommandId = "load-snapshot";

// Dev-only: reset the device-local onboarding/coaching flags (tourSeen + the
// tips opt-out). Present only when the
// shell wires `onResetOnboarding` (import.meta.env.DEV).
const resetOnboardingCommandId = "dev-reset-onboarding";

export type OmnibarHostProps = {
    // The workspace's library — the shared read-model every jump row and
    // authoring-verb gates from.
    readonly library: Library | null;
    readonly activeMaterialId: string | null;
    // The single-authority summon gate (AppShellHost §3) — panel-command rows
    // only list a slot when it's available, same as the toolbar + accelerators.
    readonly slotAvailability: Record<SecondarySlot, SlotAvailability>;
    readonly kbdPlatform: KbdPlatform;
    // Whether the command surface is the frame's current overlay — gates the
    // in-surface ⌘1..⌘9 digit accelerator so it never fires while closed.
    readonly commandSurfaceOpen: boolean;
    // Resolve a selected id back to the resource it names — the shared
    // library index the shell already derives (`allRefs.find`).
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    readonly summonSlot: (slot: SecondarySlot) => void;
    // Opens the selected/jumped-to resource (§4a: the ONLY way OmnibarHost
    // reaches toward Editor/Chat — through this dispatch, never directly).
    readonly openRef: (ref: ResourceRef) => void;
    readonly enterFullChat: () => void;
    // Start a fresh conversation — same-command with the rail's pinned-top
    // icon and ⌘T (never a bespoke omnibar-only path).
    readonly onNewConversation: () => void;
    readonly gotoSettings: () => void;
    readonly openOverlay: (overlay: OverlaySurface) => void;
    readonly closeOverlay: () => void;
    readonly openCreateDialog: (seedTitle?: string) => void;
    readonly openFilePicker: () => void;
    // State-snapshot save/load (VCR replacement). Two distinct intents: save
    // downloads the current durable state as JSON; load picks a file, seeds the
    // state cell from it, and re-projects. Both are impure DOM/network I/O the
    // shell fulfils through the snapshot port.
    readonly onSaveSnapshot: () => void;
    readonly onLoadSnapshot: () => void;
    // Dev-only: reset the device-local onboarding flags so the first-run
    // coaching overlay shows again. Present only under `import.meta.env.DEV`.
    readonly onResetOnboarding?: () => void;
};

export type OmnibarHostApi = {
    // The bar input's live value + the handlers TopStrip wires its combobox
    // input through.
    readonly commandQuery: string;
    readonly onCommandQueryChange: (query: string) => void;
    readonly onCommandKeyDown: (
        event: ReactKeyboardEvent<HTMLInputElement>,
    ) => void;
    // The ARIA wiring TopStrip's bar and the results listbox share.
    readonly commandActiveId: string | null;
    readonly commandListboxId: string;
    readonly commandOptionId: (id: string) => string;
    readonly commandHasResults: boolean;
    // The pure results listbox TopStrip anchors flush beneath the bar.
    readonly commandSurface: ReactNode;
    // Open the surface seeded with a query — empty for the omnibar bar + ⌘K,
    // `@kind: ` for the rail's per-section "Show all" doors.
    readonly openCommand: (seed: string) => void;
    readonly openShowAll: (kind: ResourceKind) => void;
    // Clear the query/cursor/history-recall WITHOUT closing the overlay — the
    // shell composes this with its own `closeOverlay()` at every dismissal
    // site (Esc, outside-click, a row picked).
    readonly resetCommandSurface: () => void;
};

export const useOmnibarHost = (props: OmnibarHostProps): OmnibarHostApi => {
    const {
        library,
        activeMaterialId,
        slotAvailability,
        kbdPlatform,
        commandSurfaceOpen,
        refFor,
        summonSlot,
        openRef,
        enterFullChat,
        onNewConversation,
        gotoSettings,
        openOverlay,
        closeOverlay,
        openCreateDialog,
        openFilePicker,
        onSaveSnapshot,
        onLoadSnapshot,
        onResetOnboarding,
    } = props;
    const { t } = useLingui();

    // The ONE command surface's live query (P17) — LIFTED so the omnibar BAR
    // (the combobox input, rendered by TopStrip) and the results listbox
    // (CommandPalette, rendered here) share ONE query. Entry points seed it
    // (the rail's per-section search opens `type:<kind> `; the ⌘K chord + the
    // omnibar open it empty), then the bar input drives it. Cleared when the
    // surface closes.
    const [commandQuery, setCommandQuery] = useState("");
    // The command surface's active-option cursor (the bar's
    // aria-activedescendant target). Null until an Arrow key moves it; the
    // effective active option falls back to the first row (below), so Enter
    // opens the top hit.
    const [commandPicked, setCommandPicked] = useState<string | null>(null);
    // The omnibar's shell-style history recall (ArrowUp = older submission).
    // One capped ring + a cursor, both in the pure `omnibarHistory` model;
    // the key handler consults it before listbox navigation. SHORTCUT
    // (lifecycle): session-only — thrown away on reload; persistence
    // (localStorage / the event log) is a later additive step (see
    // omnibarHistory.ts).
    const [omnibarHistory, setOmnibarHistory] =
        useState<OmnibarHistory>(emptyOmnibarHistory);
    // The results listbox element id — shared: the bar input's
    // `aria-controls` and the CommandPalette listbox's `id`, so the combobox
    // wiring points at exactly one popup.
    const commandListboxId = useId();

    // Clear the query/cursor/history-recall — used on every dismissal (a row
    // picked, Esc, outside-click). Does NOT close the overlay: that's a frame
    // dispatch the caller composes alongside this (§4a — OmnibarHost never
    // reaches into the frame beyond the callbacks it was handed).
    const resetCommandSurface = () => {
        setCommandQuery("");
        setCommandPicked(null);
        setOmnibarHistory(resetOmnibarCursor);
    };

    // Open the ONE command surface seeded with `seed` (empty for the omnibar
    // + ⌘K, `@kind: ` for the rail's per-kind "Visa alla" doors — the
    // omnibar's sigil DSL, omni.ts): set the shared query, reset the cursor,
    // and float the dropdown. The bar input takes focus from TopStrip's own
    // open→ focus effect. The single entry the omnibar and the ⌘K chord share.
    const openCommand = (seed: string) => {
        setCommandQuery(seed);
        setCommandPicked(null);
        // A fresh open never resumes a mid-recall cursor (history persists,
        // the walk position does not).
        setOmnibarHistory(resetOmnibarCursor);
        openOverlay({ type: "command" });
    };
    // The rail's per-kind "Visa alla" (bug fix 2026-08-19): open the omnibar
    // pre-filtered to ONE resource kind via the `@kind:` sigil.
    const openShowAll = (kind: ResourceKind) => openCommand(`@${kind}: `);

    // The palette's panel commands (master audit R1): summon a right-panel
    // slot from anywhere. Only slots that apply to the active view are
    // listed — a palette row must never be a silent no-op.
    const panelCommandTitle = (slot: SecondarySlot): string => {
        switch (slot) {
            case "assistant":
                return t`Open the assistant`;
            case "review":
                return t`Open the review panel`;
            case "grounding":
                return t`Open the grounding panel`;
            case "panel":
                return t`Open context`;
            default:
                return exhaustSwitchCase(slot);
        }
    };
    const panelCommandItems: readonly CommandItem[] = secondarySlots
        .filter((slot) => slotAvailability[slot].type === "available")
        .map(
            (slot): CommandItem => ({
                id: panelCommandId[slot],
                title: panelCommandTitle(slot),
                kind: "command",
            }),
        );

    // The authoring verbs, split by the scope they need. The annotation on each
    // const contextually types the literals, so `kind: "command"` stays a
    // CommandKind without an `as` cast. Workspace-scoped creation is ALWAYS
    // available now (flattened-object-model-design.md — no "no active
    // project" gate left to check).
    const workspaceAuthoringVerbs: readonly CommandItem[] = [
        { id: newMaterialCommandId, title: t`New material`, kind: "command" },
        { id: addSourceCommandId, title: t`Add source`, kind: "command" },
        {
            id: newConversationCommandId,
            title: t`New conversation`,
            kind: "command",
        },
    ];
    const materialAuthoringVerbs: readonly CommandItem[] = [
        {
            id: exportMaterialCommandId,
            title: t`Export material`,
            kind: "command",
        },
    ];

    // Command-palette items: the workspace's flat pool of resources, per the
    // rail's order, with the ambient conversation, panel commands, authoring
    // verbs and app commands trailing. The per-map return annotation keeps the
    // `kind` literal.
    const commandItems: readonly CommandItem[] = [
        // The workspace's flat pool of resources — the SHARED corpus projection
        // (searchCorpus.ts), reused verbatim by the in-card reference search so
        // the two surfaces fold one corpus and cannot drift.
        ...libraryCommandItems(library, t`Untitled`),
        // The ambient conversation (master audit R6, flattened-object-model
        // §5.1): a real flat-pool object now, findable like any other kind.
        // Today's model holds exactly one (conversation.md — a list widens
        // this when multi-conversation lands), always reachable.
        {
            id: chatFullCommandId,
            title: t`Assistant`,
            kind: "conversation",
        },
        ...panelCommandItems,
        // Authoring verbs (Blocker A), availability-gated so no row is a silent
        // no-op: export needs an active material; material/source creation is
        // always available (workspace-scoped, no project gate).
        ...workspaceAuthoringVerbs,
        ...(activeMaterialId !== null ? materialAuthoringVerbs : []),
        // Settings — the singleton tab, reachable from the omnibar like any
        // verb (same-command: dispatches open-settings).
        {
            id: settingsCommandId,
            title: t`Settings`,
            kind: "command",
        },
        // History & Inspection — the timeline surface, reachable from the
        // omnibar (same overlay the workspace band + resource overflow open).
        {
            id: historyCommandId,
            title: t`History`,
            kind: "command",
        },
        // The Diátaxis help overlay — surfaced here so it is discoverable.
        {
            id: helpCommandId,
            title: t`Help`,
            kind: "command",
        },
        // State snapshot save/load (VCR replacement) — always available; two
        // distinct verbs routed to the shell's snapshot port.
        {
            id: saveSnapshotCommandId,
            title: t`Save snapshot`,
            kind: "command",
        },
        {
            id: loadSnapshotCommandId,
            title: t`Load snapshot`,
            kind: "command",
        },
        // Dev-only: reset onboarding so the first-run coaching overlay returns.
        ...(onResetOnboarding !== undefined
            ? [
                  {
                      id: resetOnboardingCommandId,
                      title: "Reset onboarding (coaching)",
                      kind: "dev-command" as const,
                  },
              ]
            : []),
    ];

    // === The ONE command combobox ==========================================
    // The omnibar bar is a TRIGGER; the CommandPalette dropdown ENCLOSES the
    // single combobox input at its top with the listbox below. The input +
    // listbox share the lifted `commandQuery` + this cursor. The SAME pure
    // `commandResults` the palette renders with is called here to size + move
    // the cursor, so the input and the listbox never disagree on the rows.
    const commandOptionId = (id: string): string =>
        `${commandListboxId}-opt-${id}`;
    const { flatIds: commandFlatIds } = commandResults(
        commandItems,
        commandQuery,
    );
    // The effective active option: the picked row if still visible, else the
    // first — so Enter (and the bar's aria-activedescendant) always target a
    // real row. Null only when there are no rows (the empty state showing).
    const commandActiveId: string | null =
        commandPicked !== null && commandFlatIds.includes(commandPicked)
            ? commandPicked
            : (commandFlatIds[0] ?? null);

    // A palette row's jump. Panel-command rows summon a right-panel slot
    // (returning to the project world first — the panels read it); project
    // rows switch project; resource rows open a tab. Projects are checked
    // before resources — project ids and resource ids come from disjoint
    // upstream id spaces, so that order is belt-and-braces, not a tiebreak.
    const onCommandSelect = (id: string) => {
        // Selecting a row closes the surface — clear the shared query +
        // cursor so the resting bar shows its placeholder, not the last
        // search.
        resetCommandSurface();
        closeOverlay();
        if (id === chatFullCommandId) {
            enterFullChat();
            return;
        }
        if (id === settingsCommandId) {
            gotoSettings();
            return;
        }
        if (id === historyCommandId) {
            // Replaces the palette overlay with the History surface (one
            // floating surface at a time — `overlay` swaps).
            openOverlay({ type: "history" });
            return;
        }
        // Authoring verbs (Blocker A): route to the SAME handlers the rail /
        // desktop menu fire. The overlay already closed above; each opens its own
        // dialog / picker / overlay.
        if (id === newMaterialCommandId) {
            openCreateDialog();
            return;
        }
        if (id === addSourceCommandId) {
            openFilePicker();
            return;
        }
        if (id === newConversationCommandId) {
            onNewConversation();
            return;
        }
        if (id === exportMaterialCommandId) {
            if (activeMaterialId !== null)
                openOverlay({ type: "export", materialId: activeMaterialId });
            return;
        }
        if (id === helpCommandId) {
            openOverlay({ type: "help" });
            return;
        }
        if (id === saveSnapshotCommandId) {
            onSaveSnapshot();
            return;
        }
        if (id === loadSnapshotCommandId) {
            onLoadSnapshot();
            return;
        }
        if (id === resetOnboardingCommandId) {
            onResetOnboarding?.();
            return;
        }
        const slot = panelSlotForCommandId(id);
        if (slot !== undefined) {
            // The one summon path (D2): availability-gated.
            summonSlot(slot);
            return;
        }
        const ref = refFor(id);
        if (ref !== undefined) openRef(ref);
    };

    // A fresh query resets the cursor — the effective active falls back to the
    // new first row.
    const onCommandQueryChange = (query: string) => {
        setCommandQuery(query);
        setCommandPicked(null);
        // Typing detaches any in-flight history recall (the edited text is now
        // the live query; the next ArrowUp starts a fresh walk from the newest).
        setOmnibarHistory(resetOmnibarCursor);
    };
    // Keyboard on the enclosed input (single focus point): Arrow/Home/End move
    // the cursor over the listbox rows (no wrap — past an edge the active id
    // stays put, the listbox convention), Enter activates the active row or, on
    // zero matches, offers creation. Esc is left to the Popover (dismissal).
    const onCommandKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
        // ⌘1..⌘9 (⌃ on non-mac) while the surface is OPEN: activate the Nth
        // visible result through the SAME path Enter uses. Scoped tight — it
        // fires only when the command overlay is open (this handler already only
        // runs with the bar input focused), and `stopPropagation` keeps it from
        // falling through to the GLOBAL select-tab chord (⌘N = Nth visible tab),
        // which stays untouched when the surface is closed. Out of range (fewer
        // than N results) no-ops rather than switching tabs. `event.code`
        // (Digit1..Digit9) so the mapping is layout-stable.
        const accelMod = kbdPlatform === "mac" ? event.metaKey : event.ctrlKey;
        if (
            commandSurfaceOpen &&
            accelMod &&
            !event.altKey &&
            !event.shiftKey &&
            /^Digit[1-9]$/.test(event.code)
        ) {
            event.preventDefault();
            event.stopPropagation();
            const digit = Number(event.code.slice("Digit".length));
            const picked = commandResultForDigit(commandFlatIds, digit);
            if (picked !== null) onCommandSelect(picked);
            return;
        }
        const last = commandFlatIds.length - 1;
        const at =
            commandActiveId !== null
                ? commandFlatIds.indexOf(commandActiveId)
                : -1;
        // History recall (shell-style): ArrowUp walks to older SUBMITTED
        // queries, ArrowDown back to newer / the live input. It takes over the
        // arrows ONLY while a recall is in flight OR from an empty query with a
        // non-empty history — so the common "type, then arrow to a result" flow
        // keeps listbox navigation untouched. While recalling, the arrows are
        // owned by history (you re-run what you ran; Enter opens the top hit).
        const recalling = omnibarHistory.cursor !== null;
        const wantRecallOlder =
            recalling ||
            (commandQuery === "" && omnibarHistory.entries.length > 0);
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                if (recalling) {
                    const step = recallNewer(omnibarHistory);
                    if (step.type === "recalled") {
                        setOmnibarHistory(step.history);
                        setCommandQuery(step.value);
                        setCommandPicked(null);
                    }
                    break;
                }
                setCommandPicked(
                    commandFlatIds[Math.min(at + 1, last)] ?? null,
                );
                break;
            case "ArrowUp":
                event.preventDefault();
                if (wantRecallOlder) {
                    const step = recallOlder(omnibarHistory);
                    if (step.type === "recalled") {
                        setOmnibarHistory(step.history);
                        setCommandQuery(step.value);
                        setCommandPicked(null);
                    }
                    break;
                }
                setCommandPicked(commandFlatIds[Math.max(at - 1, 0)] ?? null);
                break;
            case "Home":
                event.preventDefault();
                setCommandPicked(commandFlatIds[0] ?? null);
                break;
            case "End":
                event.preventDefault();
                setCommandPicked(commandFlatIds[last] ?? null);
                break;
            case "Enter": {
                event.preventDefault();
                const createTitle = commandQuery.trim();
                // Record the SUBMITTED query (keyboard Enter, both the pick and
                // the create-on-zero paths) — recognition-driven picks (pointer,
                // ⌘-digit) are not recall-worthy and never reach here.
                if (createTitle !== "")
                    setOmnibarHistory((h) =>
                        recordOmnibarQuery(h, createTitle),
                    );
                if (commandActiveId !== null) onCommandSelect(commandActiveId);
                else if (createTitle !== "") {
                    setCommandQuery("");
                    setCommandPicked(null);
                    openCreateDialog(createTitle);
                }
                break;
            }
            default:
                break;
        }
    };

    // The ONE command surface — the pure results listbox the TopStrip anchors
    // FLUSH under the omnibar BAR (the combobox input) as one continuous card.
    // It renders + filters the shared `commandQuery` and reports the chosen id
    // UP; the input itself lives in the bar (the input's change + keyboard route
    // through the lifted `onCommandQueryChange` / `onCommandKeyDown`, so all
    // three entry points share one query). The active-option cursor is
    // host-owned, shared via `commandActiveId` + the `commandOptionId` /
    // `commandListboxId` ARIA wiring. The Popover owns Esc + outside-click.
    const commandSurface: ReactNode = (
        <CommandPalette
            items={commandItems}
            query={commandQuery}
            activeId={commandActiveId}
            listboxId={commandListboxId}
            optionId={commandOptionId}
            // `onCommandSelect` closes the surface (clearing the query) and may
            // SWAP in another overlay (the History door) — the sole closer.
            onSelect={onCommandSelect}
            onCreateFromQuery={(title) => {
                // Enter-on-zero (P17): close the loop back to creation — open the
                // create dialog seeded with the typed title.
                setCommandQuery("");
                setCommandPicked(null);
                openCreateDialog(title);
            }}
            // The sigil-DSL ghost/hint rows refill the bar with a suggested
            // query (e.g. `@s:`); route it through the same query setter typing
            // uses (which also detaches history recall).
            onFillQuery={onCommandQueryChange}
            platform={kbdPlatform}
        />
    );

    return {
        commandQuery,
        onCommandQueryChange,
        onCommandKeyDown,
        commandActiveId,
        commandListboxId,
        commandOptionId,
        commandHasResults: commandFlatIds.length > 0,
        commandSurface,
        openCommand,
        openShowAll,
        resetCommandSurface,
    };
};
